"use client";
import React, { useCallback, useEffect, useRef, useState } from "react";
import gsap from "gsap";

import { LinkElement } from "../link/link";
import { ServiceListBlock as ListBlock } from "../../../sanity/lib/types/serviceListBlock";

import useIsMobile from "../../hooks/useIsMobile";
import { useAppContext } from "../../provider/AppProvider";
import { ImageRepeater } from "../ImageRepeater";

export default function ({ listBlock }: { listBlock: ListBlock }) {
    const [active, setActive] = useState(-1);
    const section = useRef<HTMLElement>(null);
    const dot = useRef<HTMLDivElement>(null);
    const [dotVisible, setDotVisible] = useState<boolean>(true);

    const { isMobile } = useIsMobile();
    const { setCursorState } = useAppContext();

    useEffect(() => {
        if (!section?.current) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setCursorState("hidden");
                    setDotVisible(true);
                    setActive(0);
                } else {
                    setCursorState("default");
                    setDotVisible(false);
                }
            },
            { root: null, threshold: 0.5 },
        );

        observer.observe(section.current);
    }, []);

    const onMouseEnter = useCallback(
        (index: number) => {
            if (isMobile) return;
            if (index === active) return;
            setActive(index);
        },
        [active, isMobile],
    );

    useEffect(() => {
        if (!dotVisible && !dot.current) return;

        const activeEl = document.querySelector(
            `.service-list-block-title-wrapper[data-index-desk="${active}"]`,
        );

        if (!activeEl) return;

        const dotPlaceholder = activeEl?.querySelector(".dot-placeholder") as HTMLElement;

        gsap.to(dot.current, {
            top: dotPlaceholder.offsetTop - 12,
            left: dotPlaceholder.offsetLeft - 12,
            duration: 0.3,
            ease: "power4.out",
        });
    }, [dotVisible, active, isMobile]);

    useEffect(() => {
        if (isMobile) openAccordion(0, true);
    }, [isMobile]);

    const openAccordion = useCallback(
        (index: number, isInitial: boolean = false) => {
            if (!isMobile) return;
            if (index === active && !isInitial) return;

            setActive(index);

            const activeAccordion = document.querySelector(
                `.service-list-block-content-mobile.active`,
            );
            const newActiveAccordion = document.querySelector(
                `.service-list-block-content-mobile[data-index-mobile="${index}"]`,
            );

            gsap.set(activeAccordion, { height: 0 });
            gsap.to(newActiveAccordion, {
                height: "auto",
                duration: 0.3,
                ease: "power4.out",
                delay: 0,
            });
        },
        [active, isMobile],
    );

    return (
        <section ref={section} className="py-14 md:py-20 service-list-block">
            <div className="container pb-4 md:pb-2 md:border-b md:border-black">
                <div className="mx-2.5 md:mx-5">
                    <h2 className="m-body-l md:d-headline-2">{listBlock.title}</h2>
                </div>
            </div>

            <div className="container">
                <div className="mx-2.5 md:mx-5">
                    <div className="relative grid grid-cols-12 gap-5 md:mt-19 md:mb-9">
                        <div className="relative flex flex-col col-span-12 gap-3 md:col-span-7 xl:col-span-5 md:gap-0">
                            <div
                                ref={dot}
                                className={`absolute translate-y-5 -translate-x-2 md:-translate-y-0 md:translate-x-0 w-9 h-9 md:w-12 md:h-12 rounded-full transition-opacity dot bg-black ${dotVisible || isMobile ? "opacity-100" : "opacity-0"}`}
                            ></div>
                            {listBlock.services?.map((service, index) => (
                                <div
                                    className="service-list-block-title-wrapper"
                                    data-index-desk={index}
                                    key={index}
                                >
                                    <button
                                        onMouseEnter={() => onMouseEnter(index)}
                                        onClick={() => openAccordion(index)}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter") {
                                                setActive(index);
                                            }
                                        }}
                                        className="flex items-start justify-between w-full gap-2 md:w-auto md:justify-baseline md:items-center md:gap-5 service-list-block-title"
                                    >
                                        <div className="flex gap-2 md:gap-5">
                                            <span className="mt-2.5 md:mt-0 m-body-l md:d-headline-4">
                                                0{index + 1}
                                            </span>
                                            <h3 className="cursor-default m-display-3 md:d-headline-1 lg:d-display-2">
                                                {service.serviceReference.service.title}
                                            </h3>
                                        </div>
                                        <span
                                            data-placeholder={index}
                                            className="w-4 h-4 dot-placeholder"
                                        ></span>
                                    </button>

                                    {isMobile && (
                                        <div
                                            data-index-mobile={index}
                                            className={`h-0 w-full overflow-hidden pl-7.5 service-list-block-content-mobile ${active === index ? "active" : ""}`}
                                        >
                                            <div>
                                                <div className="pt-4.5 pb-6">
                                                    {service.serviceReference.categories?.map(
                                                        (category, catIndex) => (
                                                            <p key={catIndex} className="d-body-m">
                                                                {category}
                                                            </p>
                                                        ),
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                        {!isMobile && (
                            <div className="relative grid w-full grid-cols-7 col-span-7 pt-5 md:col-span-4 xl:col-span-7 md:flex md:flex-col xl:grid gap-y-2">
                                <div className="relative md:col-span-3 xl:col-start-2 banner:col-start-1 lg:col-span-7 xl:col-span-3 md:order-2 xl:order-1 lg:order-2">
                                    {listBlock.services?.map((service, index) => (
                                        <div
                                            key={index}
                                            data-index={index}
                                            className={`absolute col-span-3 gap-5 service-list-block-content ${index === active ? "active" : ""}`}
                                        >
                                            <div className="">
                                                {service.serviceReference.categories?.map(
                                                    (category, catIndex) => (
                                                        <p key={catIndex} className="d-body-m">
                                                            {category}
                                                        </p>
                                                    ),
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                <div
                                    className={`relative col-span-3 lg:col-span-7 xl:col-span-3 overflow-hidden md:order-1 lg:order-1 xl:order-2`}
                                >
                                    <div className="aspect-[334/188] relative overflow-hidden">
                                        {listBlock.services?.map((service, index) => (
                                            <div
                                                key={index}
                                                className={`absolute top-0 left-0 overflow-hidden transition-transform duration-500 ${active === index ? "translate-y-0" : " translate-y-full"}`}
                                            >
                                                {service.customImages && (
                                                    <ImageRepeater
                                                        className="object-contain aspect-[334/188]"
                                                        width={500}
                                                        images={service.customImages}
                                                    />
                                                )}
                                                {!service.customImages &&
                                                    service.serviceReference.service.images && (
                                                        <ImageRepeater
                                                            className="object-contain aspect-[334/188]"
                                                            width={500}
                                                            images={
                                                                service.serviceReference.service
                                                                    .images
                                                            }
                                                        />
                                                    )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                    <div className="flex flex-col-reverse items-start gap-5 pt-6 md:items-center md:flex-row md:pl-13">
                        {listBlock.link && <LinkElement className="link" link={listBlock.link} />}
                        {listBlock.text && (
                            <p className="d-body-m whitespace-break-spaces">{listBlock.text}</p>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
}
